import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RevenueAnalyticsChart = ({ data = [] }) => {
  const [chartView, setChartView] = useState('comparison'); // comparison, forecast, actual

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })?.format(value);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-3 card-shadow">
          <p className="text-sm font-medium text-foreground">{`${label} 2025`}</p>
          {payload?.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry?.color }}>
              {`${entry?.dataKey?.charAt(0)?.toUpperCase() + entry?.dataKey?.slice(1)}: ${formatCurrency(entry?.value)}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const getChartLines = () => {
    switch (chartView) {
      case 'forecast':
        return (
          <>
            <Line 
              type="monotone" 
              dataKey="forecast" 
              stroke="#3B82F6" 
              strokeWidth={2}
              dot={{ fill: '#3B82F6', strokeWidth: 0, r: 4 }}
              activeDot={{ r: 6, stroke: '#3B82F6', strokeWidth: 2 }}
            />
            <Line 
              type="monotone" 
              dataKey="revenue" 
              stroke="#10B981" 
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={{ fill: '#10B981', strokeWidth: 0, r: 4 }}
              activeDot={{ r: 6, stroke: '#10B981', strokeWidth: 2 }}
            />
          </>
        );
      case 'actual':
        return (
          <Line 
            type="monotone" 
            dataKey="actual" 
            stroke="#8B5CF6" 
            strokeWidth={3}
            dot={{ fill: '#8B5CF6', strokeWidth: 0, r: 5 }}
            activeDot={{ r: 7, stroke: '#8B5CF6', strokeWidth: 2 }}
          />
        );
      default:
        return (
          <>
            <Line 
              type="monotone" 
              dataKey="revenue" 
              stroke="#10B981" 
              strokeWidth={2}
              dot={{ fill: '#10B981', strokeWidth: 0, r: 4 }}
              activeDot={{ r: 6, stroke: '#10B981', strokeWidth: 2 }}
            />
            <Line 
              type="monotone" 
              dataKey="forecast" 
              stroke="#3B82F6" 
              strokeWidth={2}
              strokeDasharray="3 3"
              dot={{ fill: '#3B82F6', strokeWidth: 0, r: 3 }}
              activeDot={{ r: 5, stroke: '#3B82F6', strokeWidth: 2 }}
            />
            <Line 
              type="monotone" 
              dataKey="actual" 
              stroke="#8B5CF6" 
              strokeWidth={1}
              strokeDasharray="1 1"
              dot={false}
              activeDot={{ r: 4, stroke: '#8B5CF6', strokeWidth: 2 }}
            />
          </>
        );
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 card-shadow">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
            <Icon name="TrendingUp" size={20} className="text-success" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Revenue Analytics</h3>
            <p className="text-sm text-muted-foreground">Revenue trends with forecast comparison</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <div className="flex bg-muted rounded-lg p-1">
            <button
              onClick={() => setChartView('comparison')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                chartView === 'comparison' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setChartView('forecast')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                chartView === 'forecast' 
                  ? 'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Forecast
            </button>
            <button
              onClick={() => setChartView('actual')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                chartView === 'actual' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Actual
            </button>
          </div>
          <Button variant="ghost" size="sm" iconName="Download">
            Export
          </Button>
        </div>
      </div>
      
      <div className="h-80 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
            <XAxis 
              dataKey="month" 
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: 'currentColor' }}
              className="text-muted-foreground"
            />
            <YAxis 
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: 'currentColor' }}
              className="text-muted-foreground"
              tickFormatter={formatCurrency}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              verticalAlign="bottom" 
              height={36}
              iconType="line"
              wrapperStyle={{ paddingTop: '20px' }}
            />
            {getChartLines()}
            <ReferenceLine 
              y={350000} 
              stroke="#F59E0B" 
              strokeDasharray="2 2" 
              label={{ value: "Target", position: "topRight", fill: "#F59E0B" }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      
      <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-border">
        <div className="text-center">
          <div className="text-sm text-muted-foreground">Avg Revenue</div>
          <div className="text-lg font-semibold text-foreground">{formatCurrency(345000)}</div>
        </div>
        <div className="text-center">
          <div className="text-sm text-muted-foreground">Growth Rate</div>
          <div className="text-lg font-semibold text-success">+12.3%</div>
        </div>
        <div className="text-center">
          <div className="text-sm text-muted-foreground">Forecast Accuracy</div>
          <div className="text-lg font-semibold text-primary">94.2%</div>
        </div>
      </div>
    </div>
  );
};

export default RevenueAnalyticsChart;